import type { I18n, MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { useFormContext, useWatch } from 'react-hook-form';
import type { ReactNode } from 'react';
import { z } from 'zod';

import { EntityPickerField, SelectFormField, type EntitySource } from '@/shared/form';

export interface AssignValues {
  tenant: string;
  userId: string | null;
  userIdDisplay: string | null;
  groupName: string | null;
  groupNameDisplay: string | null;
}
export const ASSIGN_EMPTY: AssignValues = {
  tenant: '',
  userId: null,
  userIdDisplay: null,
  groupName: null,
  groupNameDisplay: null,
};
export interface AssignMessages {
  readonly user: MessageDescriptor;
  readonly group: MessageDescriptor;
  readonly tenant: MessageDescriptor;
}
const defaultMessages: AssignMessages = {
  user: msg`Operador: Precisa ser preenchido.`,
  group: msg`Grupo: Precisa ser preenchido.`,
  tenant: msg`Tenant: Precisa ser preenchido.`,
};
/** Operador ou grupo; rótulos de validação preservados pelo chamador (U50). */
export function assignSchema(
  i18n: I18n,
  messages: AssignMessages = defaultMessages,
): z.ZodType<AssignValues, AssignValues> {
  return z
    .object({
      tenant: z.string(),
      userId: z.string().nullable(),
      userIdDisplay: z.string().nullable(),
      groupName: z.string().nullable(),
      groupNameDisplay: z.string().nullable(),
    })
    .superRefine((value, context) => {
      if (value.userId || value.groupName) return;
      context.addIssue({ code: 'custom', path: ['userId'], message: i18n._(messages.user) });
      context.addIssue({ code: 'custom', path: ['groupName'], message: i18n._(messages.group) });
      if (!value.tenant)
        context.addIssue({ code: 'custom', path: ['tenant'], message: i18n._(messages.tenant) });
    });
}
type AssignFieldValues = Omit<AssignValues, 'tenant'> & { tenant: string | null };
interface AssignFieldsProps<U, G> {
  readonly options?: readonly { value: string; label: string }[];
  readonly users: (tenant: string | undefined) => EntitySource<U>;
  readonly groups: (tenant: string | undefined) => EntitySource<G>;
  readonly userLabel?: string;
  readonly groupLabel?: string;
  readonly userPlaceholder?: string;
  readonly groupPlaceholder?: string;
  readonly tenantField?: (reset: () => void) => ReactNode;
  readonly preserveEmptyTenant?: boolean;
}
/** Fontes pertencem ao módulo; trocar correspondente limpa operador e grupo. */
export function AssignFields<U, G>({
  options = [],
  users,
  groups,
  userLabel,
  groupLabel,
  userPlaceholder,
  groupPlaceholder,
  tenantField,
  preserveEmptyTenant = false,
}: AssignFieldsProps<U, G>) {
  const { t } = useLingui();
  const { control, setValue } = useFormContext<AssignFieldValues>();
  const tenant = useWatch({ control, name: 'tenant' });
  const scope = preserveEmptyTenant
    ? (tenant ?? undefined)
    : tenant === ''
      ? undefined
      : (tenant ?? undefined);
  const reset = () => {
    for (const name of ['userId', 'userIdDisplay', 'groupName', 'groupNameDisplay'] as const)
      setValue(name, null);
  };
  return (
    <>
      {tenantField ? (
        tenantField(reset)
      ) : (
        <SelectFormField<AssignFieldValues>
          name="tenant"
          label={t`Correspondente`}
          options={options}
          placeholder={t`Selecione`}
          onValueChange={reset}
        />
      )}
      <EntityPickerField<AssignFieldValues, U>
        name="userId"
        displayName="userIdDisplay"
        label={userLabel ?? t`Operadores`}
        {...(userPlaceholder === undefined ? {} : { placeholder: userPlaceholder })}
        source={users(scope)}
      />
      <EntityPickerField<AssignFieldValues, G>
        name="groupName"
        displayName="groupNameDisplay"
        label={groupLabel ?? t`Grupos`}
        {...(groupPlaceholder === undefined ? {} : { placeholder: groupPlaceholder })}
        source={groups(scope)}
      />
    </>
  );
}
